/**
 * Open/collapsed state for the home page's Active, Shared, Archived and Deleted
 * accordion sections (LEV-2014 step 4), persisted across visits and keyed per viewer
 * (`viewerUserId`) so a shared browser profile — or a signed-out/signed-in
 * swap on the same device — never shows one person's open section to
 * another. Falls back to the unsuffixed key when no viewer id is known.
 *
 * Active and Shared default OPEN (they hold live work); Archived and Deleted
 * default closed. Only an explicit stored `active: false` / `shared: false`
 * collapses them, so a value saved before they were collapsible still reads as open.
 *
 * Initial state is always the defaults so the first client render
 * matches the server-rendered markup exactly — a mount effect then reads the
 * stored preference and may flip sections. That produces a one-frame
 * flash on reload; it is the accepted price of avoiding a
 * hydration mismatch (React would otherwise warn and re-render from scratch).
 */
"use client";

import { useEffect, useState } from "react";

const BASE_STORAGE_KEY = "lev.home.sections.open";

const DEFAULT_OPEN_KEYS = ["active", "shared"];

interface StoredSectionsOpen {
  active: boolean;
  archived: boolean;
  deleted: boolean;
  shared: boolean;
}

/** Exported so callers (and the Storybook decorator) that seed localStorage directly can target the exact key this hook reads for a given viewer. */
export function homeSectionsStorageKey(viewerUserId: string | undefined): string {
  return viewerUserId ? `${BASE_STORAGE_KEY}.${viewerUserId}` : BASE_STORAGE_KEY;
}

function readStoredKeys(storageKey: string): string[] {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return DEFAULT_OPEN_KEYS;
    const parsed = JSON.parse(raw) as Partial<StoredSectionsOpen>;
    const keys: string[] = [];
    if (parsed.active !== false) keys.push("active");
    if (parsed.shared !== false) keys.push("shared");
    if (parsed.archived) keys.push("archived");
    if (parsed.deleted) keys.push("deleted");
    return keys;
  } catch (error) {
    /* benign: expected: localStorage unavailable in private mode, or the
       stored value predates this key's shape */
    if (process.env.NODE_ENV !== "production") {
      console.warn("[useHomeSectionsOpen] Failed to read preference", error);
    }
    return DEFAULT_OPEN_KEYS;
  }
}

function writeStoredKeys(storageKey: string, keys: string[]): void {
  try {
    const value: StoredSectionsOpen = {
      active: keys.includes("active"),
      archived: keys.includes("archived"),
      deleted: keys.includes("deleted"),
      shared: keys.includes("shared"),
    };
    window.localStorage.setItem(storageKey, JSON.stringify(value));
  } catch (error) {
    /* benign: expected: localStorage unavailable in private mode */
    if (process.env.NODE_ENV !== "production") {
      console.warn("[useHomeSectionsOpen] Failed to persist preference", error);
    }
  }
}

export function useHomeSectionsOpen(
  viewerUserId?: string,
): [string[], (keys: string[]) => void] {
  const [openKeys, setOpenKeysState] = useState<string[]>(DEFAULT_OPEN_KEYS);
  const storageKey = homeSectionsStorageKey(viewerUserId);

  useEffect(() => {
    // Always set state here, even to the defaults — `storageKey` changing means the
    // viewer changed (sign-in/sign-out on an already-mounted page), so a prior
    // viewer's open sections MUST NOT leak into the new viewer's render just
    // because the new viewer has nothing saved.
    setOpenKeysState(readStoredKeys(storageKey));
    // This is the hydration-safe read described above: it must re-run if
    // `storageKey` changes (a sign-in landing on an already-mounted page), but
    // never in response to `openKeys` itself.
  }, [storageKey]);

  const setOpenKeys = (keys: string[]) => {
    setOpenKeysState(keys);
    writeStoredKeys(storageKey, keys);
  };

  return [openKeys, setOpenKeys];
}
